import React, { useState } from 'react';
import { X, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from '../utils/toast';
import { getDeviceFingerprint } from '../utils/fingerprint';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const ReferralCodeModal = ({ isOpen, onClose, onSuccess }) => {
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setLoading(true);
    try {
      const fingerprint = await getDeviceFingerprint();
      const response = await axios.post(`${API}/referral/apply`, 
        { referral_code: referralCode.trim().toUpperCase(), device_fingerprint: fingerprint },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(response.data.message);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to apply referral code';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 overflow-y-auto"
      style={{ paddingTop: '80px', paddingLeft: '16px', paddingRight: '16px', paddingBottom: '16px' }}
    >
      <div 
        className="bg-white shadow-xl w-full relative"
        style={{ borderRadius: '12px', maxWidth: '450px', maxHeight: 'calc(100vh - 100px)', overflow: 'auto', padding: '16px' }}
      >
        <button
          onClick={onClose}
          className="absolute text-gray-400 hover:text-gray-600 transition-colors hover:bg-gray-100"
          style={{ top: '12px', right: '12px', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-purple-100 rounded-full">
            <Gift className="text-purple-600" size={24} />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Have a Referral Code?</h2>
        </div>

        <p className="text-gray-600 mb-4 text-sm">
          If a friend referred you, enter their code below to get $10 credit when you complete your first job!
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-2">
              Referral Code
            </label>
            <input
              type="text"
              id="referralCode"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="Enter code (e.g., ABC12345)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-lg tracking-wider"
              maxLength={8}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSkip}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Skip
            </button>
            <button
              type="submit"
              disabled={loading || !referralCode.trim()}
              className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Applying...' : 'Apply Code'}
            </button>
          </div>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          You can only use a referral code once. Both you and your referrer will get $10 when you complete your first job as a client.
        </p>
      </div>
    </div>
  );
};

export default ReferralCodeModal;
